import React, { useEffect, useState, useCallback } from "react";
import { Card, Row, Col, Form, Tabs, Tab, Table, Badge, Button, Spinner, Modal,Collapse } from "react-bootstrap";
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, BarChart, Bar, AreaChart, Area
} from "recharts";
import { FaChartLine, FaChartPie, FaChartBar, FaExclamationTriangle, FaEye, FaPlay, FaPause, FaChevronDown, FaChevronUp } from "react-icons/fa";
import axios from "axios";
import { BASE_URL } from "../constants";

// Palette de couleurs améliorée
const COLORS = {
  green: "#28a745",
  red: "#dc3545",
  blue: "#0d6efd",
  indigo: "#6610f2",
  teal: "#20c997"
};

// Style commun pour les cartes
const cardStyle = {
  borderRadius: '10px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  border: 'none',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.15)'
  }
};

// Style pour les en-têtes de cartes
const cardHeaderStyle = {
  backgroundColor: '#f8f9fa',
  borderBottom: '1px solid #e9ecef',
  padding: '1rem',
  borderTopLeftRadius: '10px',
  borderTopRightRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem'
};

// Style personnalisé pour les infobulles
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '10px',
        border: '1px solid #e9ecef',
        borderRadius: '5px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
      }}>
        <p style={{ margin: 0, fontWeight: 'bold' }}>{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ 
            margin: '5px 0',
            color: entry.color,
            display: 'flex',
            justifyContent: 'space-between',
            gap: '10px'
          }}>
            <span>{entry.name}:</span>
            <span style={{ color: '#212529', fontWeight: '500' }}>{entry.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function StatsTab() {
  const [callsByDay, setCallsByDay] = useState([]);
  const [verdictStats, setVerdictStats] = useState([]);
  const [errorsByAgentDay, setErrorsByAgentDay] = useState([]);
  const [agents, setAgents] = useState([]); // ✅ liste des agents
  const [dateFilter, setDateFilter] = useState({ start: "", end: "", agent: "" });
  const [callsByCategory, setCallsByCategory] = useState([]);
  const [activeTab, setActiveTab] = useState("charts");
  const [analysesWithErrors, setAnalysesWithErrors] = useState([]);
  const [loadingErrors, setLoadingErrors] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [openRow, setOpenRow] = useState(null);
  const [pagination, setPagination] = useState({
  currentPage: 1,
  perPage: 10,
  total: 0
});
  const [currentAudio, setCurrentAudio] = useState(null);
  const [currentAudioUrl, setCurrentAudioUrl] = useState(null);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);

  // Fonction pour afficher le modal
  const handleShowModal = (content, title) => {
    setModalContent(content);
    setModalTitle(title);
    setShowModal(true);
  };
const toggleRow = useCallback((idx) => {
    setOpenRow(openRow === idx ? null : idx);
  }, [openRow]);

  // Fonction pour réinitialiser les filtres
  const resetFilters = () => {
    setDateFilter({ start: "", end: "", agent: "" });
  };

  const handlePlayAudio = (audioUrl) => {
    if (!audioUrl) return;
    if (currentAudio && currentAudioUrl === audioUrl) {
      if (currentAudio.paused) {
        currentAudio.play();
      } else {
        currentAudio.pause();
      }
      return;
    }
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }
    const audio = new Audio(audioUrl);
    setAudioProgress(0);
    setAudioDuration(0);
    setCurrentAudio(audio);
    setCurrentAudioUrl(audioUrl);
    audio.play();
  };

  useEffect(() => {
    if (!currentAudio) return;
    const onTimeUpdate = () => setAudioProgress(currentAudio.currentTime || 0);
    const onLoadedMetadata = () => setAudioDuration(Number.isFinite(currentAudio.duration) ? currentAudio.duration : 0);
    const onEnded = () => {
      setCurrentAudio(null);
      setCurrentAudioUrl(null);
      setAudioProgress(0);
    };
    const onPlay = () => setAudioProgress(currentAudio.currentTime || 0);
    const onPause = () => setAudioProgress(currentAudio.currentTime || 0);

    currentAudio.addEventListener('timeupdate', onTimeUpdate);
    currentAudio.addEventListener('loadedmetadata', onLoadedMetadata);
    currentAudio.addEventListener('ended', onEnded);
    currentAudio.addEventListener('play', onPlay);
    currentAudio.addEventListener('pause', onPause);

    return () => {
      currentAudio.removeEventListener('timeupdate', onTimeUpdate);
      currentAudio.removeEventListener('loadedmetadata', onLoadedMetadata);
      currentAudio.removeEventListener('ended', onEnded);
      currentAudio.removeEventListener('play', onPlay);
      currentAudio.removeEventListener('pause', onPause);
      if (currentAudio) currentAudio.pause();
    };
  }, [currentAudio]);

  const handleSeek = (value) => {
    const v = parseFloat(value);
    if (currentAudio && Number.isFinite(v)) {
      currentAudio.currentTime = v;
      setAudioProgress(v);
    }
  };

  const formatTime = (seconds) => {
    const s = Math.max(0, Math.floor(seconds || 0));
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  // 🔹 Charger les stats des appels depuis l’API Laravel
  const fetchCallsByDay = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.get(`${BASE_URL}/stats-by-day`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start: dateFilter.start || undefined,
          end: dateFilter.end || undefined,
          agent: dateFilter.agent || undefined,
        },
      });
      setCallsByDay(res.data.callsByDay || []);
    } catch (err) {
      console.error("Erreur récupération des appels :", err);
    }
  };

  // 🔹 Répartition des verdicts (PieChart)
  const fetchVerdictStats = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.get(`${BASE_URL}/stats/verdict`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start: dateFilter.start || undefined,
          end: dateFilter.end || undefined,
          agent: dateFilter.agent || undefined,
        },
      });
      setVerdictStats(res.data.verdictStats || []);
    } catch (err) {
      console.error("Erreur récupération des verdicts :", err);
    }
  };

  // 🔹 Récupérer les erreurs totales par code
  const fetchErrorsByAgentDay = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.get(`${BASE_URL}/errors-count`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start: dateFilter.start || undefined,
          end: dateFilter.end || undefined,
          agent: dateFilter.agent || undefined,
        },
      });
      setErrorsByAgentDay(res.data || []);
    } catch (err) {
      console.error("Erreur récupération erreurs :", err);
    }
  };

  const fetchCallsByCategory = async () => {
  try {
    const token = localStorage.getItem("auth_token");
    const res = await axios.get(`${BASE_URL}/stats/categories`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        start: dateFilter.start || undefined,
        end: dateFilter.end || undefined,
        agent: dateFilter.agent || undefined,
      },
    });
    // Filter out categories that contain brackets [ ] or have empty/null labels
    const filteredCategories = (res.data.callsByCategory || []).filter(
      category => category.categorie && 
                  category.categorie.trim() !== '' && 
                  !category.categorie.includes('[') && 
                  !category.categorie.includes(']')
    );
    setCallsByCategory(filteredCategories);
  } catch (err) {
    console.error("Erreur stats catégorie :", err);
  }
};

  // 🔹 Charger la liste des agents depuis Ringover
  const fetchAgents = async () => {
    try {
      const token = "66ccd6b118f2dc31be67e8e2bcfc0099da653662"; // ou depuis localStorage
      const res = await axios.get("https://public-api.ringover.com/v2/users", {
        headers: { Authorization: token },
      });
      const agentFirstnames = res.data.list.map((user) => user.firstname);
      setAgents(agentFirstnames);
    } catch (err) {
      console.error("Erreur chargement agents :", err);
    }
  };

  // 🔹 Récupérer les analyses avec erreurs
  const fetchAnalysesWithErrors = async (page = 1) => {
    setLoadingErrors(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.get(`${BASE_URL}/analyses/errors`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start: dateFilter.start || undefined,
          end: dateFilter.end || undefined,
          agent: dateFilter.agent || undefined,
          page: page,
          per_page: 15
        },
      });
      setAnalysesWithErrors(res.data.data || []);
      setPagination(prev => ({
        ...prev,
        currentPage: res.data?.current_page || page,
        total: res.data?.total || 0
      }));
    } catch (err) {
      console.error("Erreur récupération analyses avec erreurs :", err);
      setAnalysesWithErrors([]);
    } finally {
      setLoadingErrors(false);
    }
  };

  const handlePageChange = (page) => {
  fetchAnalysesWithErrors(page);
};
  useEffect(() => {
    fetchCallsByDay();
    fetchVerdictStats();
    fetchErrorsByAgentDay(); // ✅ erreur totale
    fetchAgents(); // ✅ chargement des agents au montage
    fetchCallsByCategory();
    fetchAnalysesWithErrors(1);
  }, [dateFilter]);

  return (
    <>
      {/* 🔹 Filtres dates + agent */}
      <Card className="mb-4" style={cardStyle}>
        <div style={cardHeaderStyle}>
          <i className="bi bi-funnel-fill me-2"></i>
          <h5 className="mb-0">Filtres</h5>
        </div>
        <Card.Body>
          <Form>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-medium text-muted">Date de début</Form.Label>
                  <Form.Control
                    type="date"
                    className="form-control-lg"
                    value={dateFilter.start}
                    onChange={(e) => setDateFilter(prev => ({ ...prev, start: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-medium text-muted">Date de fin</Form.Label>
                  <Form.Control
                    type="date"
                    className="form-control-lg"
                    value={dateFilter.end}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setDateFilter(prev => ({ ...prev, end: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-medium text-muted">Agent</Form.Label>
                  <Form.Select
                    className="form-control-lg"
                    value={dateFilter.agent}
                    onChange={(e) => setDateFilter(prev => ({ ...prev, agent: e.target.value }))}
                  >
                    <option value="">Tous les agents</option>
                    {agents.map((agent, idx) => (
                      <option key={idx} value={agent}>{agent}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="d-flex flex-column h-100">
                  <Form.Label className="fw-medium text-muted">&nbsp;</Form.Label>
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary w-100 form-control-lg d-flex align-items-center justify-content-center"
                    onClick={resetFilters}
                    style={{
                      gap: '8px',
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}
                  >
                    <i className="bi bi-arrow-repeat" style={{ fontSize: '1rem' }}></i>
                    <span>Réinitialiser</span>
                  </button>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Onglets pour les graphiques et le tableau d'erreurs */}
      <Card style={cardStyle}>
        <div style={cardHeaderStyle}>
          <h5 className="mb-0">Tableau de bord</h5>
        </div>
        <Card.Body className="p-0">
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-0"
            style={{ borderBottom: 'none' }}
          >
            <Tab eventKey="charts" title={
              <span>
                <FaChartLine className="me-2" />
                Graphiques
              </span>
            }>
              <div className="p-4">
                <Row className="g-4">
        {/* 1️⃣ LineChart - Appels totaux */}
        <Col md={6}>
          <Card className="h-100" style={cardStyle}>
            <div style={cardHeaderStyle}>
              <FaChartLine className="text-primary" />
              <h5 className="mb-0">Activité des appels</h5>
            </div>
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={callsByDay} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.blue} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={COLORS.blue} stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f1f1" />
                  <XAxis 
                    dataKey="day" 
                    tick={{ fill: '#6c757d' }}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={10}
                  />
                  <YAxis 
                    tick={{ fill: '#6c757d' }}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                    tickFormatter={(value) => value.toLocaleString()}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area 
                    type="monotone" 
                    dataKey="total" 
                    name="Appels"
                    stroke={COLORS.blue}
                    fillOpacity={1} 
                    fill="url(#colorTotal)"
                    strokeWidth={2}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        {/* 2️⃣ PieChart - Répartition des verdicts */}
        <Col md={6}>
          <Card className="h-100" style={cardStyle}>
            <div style={cardHeaderStyle}>
              <FaChartPie className="text-success" />
              <h5 className="mb-0">Répartition des verdicts</h5>
            </div>
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={verdictStats}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {verdictStats.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={[
                          COLORS.green,
                          COLORS.red,
                          COLORS.blue,
                          COLORS.indigo,
                          COLORS.teal
                        ][index % 5]}
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    layout="horizontal" 
                    verticalAlign="bottom" 
                    align="center"
                    wrapperStyle={{ paddingTop: '20px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        {/* 3️⃣ BarChart - Erreurs totales par code */}
        <Col md={12}>
          <Card className="h-100" style={cardStyle}>
            <div style={cardHeaderStyle}>
              <FaChartBar className="text-danger" />
              <h5 className="mb-0">Erreurs par code</h5>
            </div>
            <Card.Body>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart 
                  data={errorsByAgentDay}
                  margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                  barSize={30}
                >
                  <defs>
                    <linearGradient id="colorErrors" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.red} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={COLORS.red} stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f1f1" />
                  <XAxis 
                    dataKey="error_code" 
                    tick={{ fill: '#6c757d' }}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={10}
                  />
                  <YAxis 
                    tick={{ fill: '#6c757d' }}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="total" 
                    name="Erreurs"
                    fill="url(#colorErrors)"
                    radius={[4, 4, 0, 0]}
                    animationDuration={1500}
                  >
                    {errorsByAgentDay.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`}
                        fill={`hsl(${index * 360 / (errorsByAgentDay.length || 1)}, 70%, 60%)`}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        {/* 4️⃣ BarChart - Appels par catégorie */}
        <Col md={12}>
          <Card className="h-100" style={cardStyle}>
            <div style={cardHeaderStyle}>
              <FaChartBar className="text-primary" />
              <h5 className="mb-0">Répartition par catégorie</h5>
            </div>
            <Card.Body>
              <ResponsiveContainer width="100%" height={550}>
                <BarChart 
                  layout="vertical"
                  data={callsByCategory}
                  margin={{ top: 30, right: 60, left: 20, bottom: 15 }}
                  barSize={100}
                >
                  <defs>
                    <linearGradient id="colorCategory" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="5%" stopColor={COLORS.indigo} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={COLORS.indigo} stopOpacity={0.2}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid horizontal={true} vertical={false} stroke="#f1f1f1" />
                  <XAxis 
                    type="number"
                    tick={{ fill: '#6c757d' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => value.toLocaleString()}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="categorie"
                    tick={{ fill: '#6c757d', fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    width={260}
                    interval={0}
                  />
                  <Tooltip 
                    content={<CustomTooltip />}
                    formatter={(value) => [value, 'Nombre d\'appels']}
                  />
                  <Bar 
                    dataKey="total" 
                    name="Appels"
                    fill="url(#colorCategory)"
                    radius={[0, 4, 4, 0]}
                    animationDuration={1500}
                  >
                    {callsByCategory.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`}
                        fill={`hsl(${index * 360 / (callsByCategory.length || 1)}, 70%, 60%)`}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
                </Row>
              </div>
            </Tab>
            
            <Tab eventKey="errors" title={
              <span>
                <FaExclamationTriangle className="me-2" />
                Erreurs d'analyse
              </span>
            }>
              <div className="p-4">
                {loadingErrors ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                    <p className="mt-2 text-muted">Chargement des erreurs...</p>
                  </div>
                ) : analysesWithErrors.length === 0 ? (
                  <div className="text-center py-4">
                    <FaExclamationTriangle className="text-muted mb-3" style={{ fontSize: '3rem' }} />
                    <p className="text-muted">Aucune erreur trouvée pour les filtres sélectionnés</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover responsive className="shadow-sm" style={{ minWidth: '1200px', '--bs-table-hover-bg': 'rgba(37, 99, 235, 0.03)' }}>
                      <thead style={{ backgroundColor: '#0d6efd', color: 'white', position: 'sticky', top: 0, zIndex: 10 }}>
                        <tr>
                          <th></th>
                          <th>Call ID</th>
                          <th>Prénom</th>
                          <th>Type de vente</th>
                          <th>Catégorie</th>
                          <th>Email</th>
                          <th>Erreurs</th>
                          <th>Nom Complet</th>
                          <th>Solutionnés</th>
                          <th>Date Création</th>
                          <th>Audio</th>
                          <th>Tran Original</th>
                          <th>Tran Traiter</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysesWithErrors?.map((row, idx) => (
                           <React.Fragment key={idx}>
                          <tr key={idx} style={{ transition: 'all 0.2s ease', '&:hover': { backgroundColor: 'rgba(37, 99, 235, 0.05)' } }}>
                             <td className="text-center" style={{ verticalAlign: 'middle' }}>
                              <Button 
                                variant="outline-primary" 
                                size="sm" 
                                onClick={() => toggleRow(idx)}
                                className="rounded-circle p-0 d-flex align-items-center justify-content-center"
                                style={{ width: '28px', height: '28px' }}
                              >
                                {openRow === idx ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                              </Button>
                            </td>
                            <td>{row.call_id}</td>
                            <td>{row.user_firstname}</td>
                            <td>{row.user_lastname}</td>
                            <td>{row.categorie}</td>
                            <td>{row.user_email}</td>
                            <td>
                              {row.errors?.length > 0 ? (
                                row.errors.map((error, errorIdx) => (
                                  <Badge key={errorIdx} bg="danger" className="me-1">
                                    {error}
                                  </Badge>
                                ))
                              ) : (
                                <Badge bg="secondary">Aucune</Badge>
                              )}
                            </td>
                            <td>{row.user_concat_name}</td>
                            <td style={{ color: row.verdict === "1" ? "green" : "red", fontWeight: "bold" }}>
                              {row.verdict === "1" ? "Oui" : "Non"}
                            </td>
                            <td>
                              {new Date(row.created_at).toLocaleDateString("fr-FR")}{" "}
                              {new Date(row.created_at).toLocaleTimeString("fr-FR", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </td>
                            <td className="text-center" style={{ verticalAlign: 'middle' }}>
                              {row.record_path ? (
                                <>
                                  <Button
                                    variant={currentAudioUrl === row.record_path && currentAudio && !currentAudio.paused ? 'outline-danger' : 'outline-primary'}
                                    size="sm"
                                    onClick={() => handlePlayAudio(row.record_path)}
                                    className="rounded-circle p-0 d-inline-flex align-items-center justify-content-center"
                                    style={{
                                      width: '28px',
                                      height: '28px',
                                      transition: 'all 0.2s ease',
                                      '&:hover': { transform: 'scale(1.1)' }
                                    }}
                                  >
                                    {currentAudioUrl === row.record_path && currentAudio && !currentAudio.paused ? (
                                      <FaPause size={12} />
                                    ) : (
                                      <FaPlay size={12} />
                                    )}
                                  </Button>
                                  {currentAudioUrl === row.record_path && currentAudio && (
                                    <div className="mt-2" style={{ minWidth: '180px' }}>
                                      <div className="d-flex justify-content-between" style={{ fontSize: '0.75rem' }}>
                                        <span>{formatTime(audioProgress)}</span>
                                        <span>{formatTime(audioDuration)}</span>
                                      </div>
                                      <input
                                        type="range"
                                        className="form-range"
                                        min={0}
                                        max={audioDuration || 0}
                                        step="0.1"
                                        value={Math.min(audioProgress, audioDuration || 0)}
                                        onChange={(e) => handleSeek(e.target.value)}
                                        style={{
                                          height: '24px',
                                          accentColor: '#16a34a',
                                          '--bs-form-range-track-bg': '#dcfce7',
                                          '--bs-form-range-thumb-bg': '#16a34a',
                                          '--bs-form-range-thumb-active-bg': '#15803d'
                                        }}
                                      />
                                    </div>
                                  )}
                                </>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td className="text-center" style={{ verticalAlign: 'middle' }}>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleShowModal(row.transcription, "Transcription Originale")}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '50px',
                                  transition: 'all 0.2s ease',
                                  '&:hover': { transform: 'scale(1.1)' }
                                }}
                              >
                                <FaEye size={14} />
                              </Button>
                            </td>
                            <td className="text-center" style={{ verticalAlign: 'middle' }}>
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleShowModal(row.transcription_traiter, "Transcription Traitée")}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '50px',
                                  transition: 'all 0.2s ease',
                                  '&:hover': { transform: 'scale(1.1)' }
                                }}
                              >
                                <FaEye size={14} />
                              </Button>
                            </td>
                          </tr>
                        <tr>
                          <td colSpan={14} className="p-0 border-0">
                            <Collapse in={openRow === idx}>
                              <div className="p-3 bg-light border-top">
                                <h6 className="mb-2 fw-bold">Détails de l'analyse :</h6>
                                <div className="bg-white p-3 rounded border">
                                  <pre style={{ 
                                    whiteSpace: "pre-wrap", 
                                    margin: 0, 
                                    fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                    fontSize: '0.85rem'
                                  }}>
                                    {row.explanation || "Aucun détail supplémentaire disponible."}
                                  </pre>
                                </div>
                              </div>
                            </Collapse>
                          </td>
                        </tr>
                          </React.Fragment>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
               {pagination.total > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3 px-3 pb-3">
              <div className="text-muted small">
                Affichage de {Math.min(10 * (pagination.currentPage - 1) + 1, pagination.total)} à {Math.min(10 * pagination.currentPage, pagination.total)} sur {pagination.total} entrées
              </div>
              <nav>
                <ul className="pagination mb-0">
                  <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1}
                    >
                      Précédent
                    </button>
                  </li>
                  {Array.from({ length: Math.ceil(pagination.total / 15) }, (_, i) => i + 1)
                    .filter(page => 
                      page === 1 || 
                      page === pagination.currentPage || 
                      page === pagination.currentPage - 1 || 
                      page === pagination.currentPage + 1 ||
                      page === Math.ceil(pagination.total / 15)
                    )
                    .map((page, i, array) => {
                      if (i > 0 && array[i-1] !== page - 1) {
                        return (
                          <React.Fragment key={`ellipsis-${i}`}>
                            <li className="page-item disabled"><span className="page-link">...</span></li>
                            <li className={`page-item ${pagination.currentPage === page ? 'active' : ''}`}>
                              <button className="page-link" onClick={() => handlePageChange(page)}>
                                {page}
                              </button>
                            </li>
                          </React.Fragment>
                        );
                      }
                      return (
                        <li key={page} className={`page-item ${pagination.currentPage === page ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => handlePageChange(page)}>
                            {page}
                          </button>
                        </li>
                      );
                    })}
                  <li className={`page-item ${pagination.currentPage * 10 >= pagination.total ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={pagination.currentPage * 10 >= pagination.total}
                    >
                      Suivant
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
            </Tab>
          </Tabs>
         
        </Card.Body>
      </Card>

      {/* Modal pour afficher les transcriptions */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ maxHeight: '400px', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
            {modalContent}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
